import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  ContentDialog,
  ContentDialogInit,
} from "../../../../components/MDDForm/components/ContentDialog.jsx";
import NewFormRenderer from "../../../../components/Form/FormDefinitionRenderer/NewFormRenderer.jsx";
import {
  FormInspectContext,
  FormPreviewModeContext,
} from "../../../../components/Form/FormDefinitionRenderer/contexts.js";
import {
  assignNodeIds,
  buildConditionDependents,
  flattenAnnotatedTree,
} from "../../../../utils/formTreeIds.js";

function SectionTemplateViewerContent({ sectionTemplate }) {
  const annotatedComponents = useMemo(
    () => assignNodeIds(sectionTemplate?.components ?? []),
    [sectionTemplate]
  );
  const flatNodes = useMemo(
    () => flattenAnnotatedTree(annotatedComponents),
    [annotatedComponents]
  );
  const dependentsByField = useMemo(
    () => buildConditionDependents(annotatedComponents),
    [annotatedComponents]
  );
  const previewValue = useMemo(
    () => ({ dependentsByField }),
    [dependentsByField]
  );

  const [selectedId, setSelectedId] = useState(null);
  const nodeRefs = useRef(new Map());

  const registerNodeRef = useCallback((id, el) => {
    if (!id) {
      return;
    }
    if (el) {
      nodeRefs.current.set(id, el);
    } else {
      nodeRefs.current.delete(id);
    }
  }, []);

  const selectNode = useCallback((id) => {
    setSelectedId(id);
    nodeRefs.current
      .get(id)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const inspectValue = useMemo(
    () => ({ selectedId, onSelectNode: selectNode, registerNodeRef }),
    [selectedId, selectNode, registerNodeRef]
  );

  if (!annotatedComponents.length) {
    return (
      <Typography color="text.secondary">
        Keine Komponenten definiert.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", gap: 3, minHeight: 400 }}>
      <Box sx={{ flex: "1 1 60%", overflow: "auto", maxHeight: "70vh" }}>
        <FormPreviewModeContext.Provider value={previewValue}>
          <FormInspectContext.Provider value={inspectValue}>
            <NewFormRenderer
              def={{ formdefinition: annotatedComponents }}
              model={{}}
              editMode={false}
              actions={[]}
            />
          </FormInspectContext.Provider>
        </FormPreviewModeContext.Provider>
      </Box>
      <Box
        sx={{
          flex: "1 1 40%",
          borderLeft: 1,
          borderColor: "divider",
          pl: 2,
          overflow: "auto",
          maxHeight: "70vh",
        }}
      >
        <Typography variant="subtitle2" color="text.secondary" mb={1}>
          Felder
        </Typography>
        <List dense disablePadding>
          {flatNodes.map((node) => {
            const isConditional = !!(
              node.hidden && typeof node.hidden === "object"
            );
            const dependents = dependentsByField.get(node.name) ?? [];
            return (
              <ListItemButton
                key={node.id}
                selected={node.id === selectedId}
                onClick={() => selectNode(node.id)}
                sx={{ pl: 1 + node.depth * 2, borderRadius: 1 }}
              >
                <ListItemText
                  primary={node.label || node.name || node.control}
                  secondary={
                    <>
                      {node.control}
                      {node.name ? ` · ${node.name}` : ""}
                      {(isConditional || dependents.length > 0) && (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                            mt: 0.5,
                          }}
                        >
                          {isConditional && (
                            <Chip
                              size="small"
                              variant="filled"
                              label="bedingt sichtbar"
                              sx={{ fontSize: "10px" }}
                            />
                          )}
                          {dependents.length > 0 && (
                            <Chip
                              size="small"
                              variant="filled"
                              color="warning"
                              sx={{ fontSize: "10px" }}
                              label={`Steuert: ${dependents.map((d) => d.label || d.name).join(", ")}`}
                            />
                          )}
                        </Box>
                      )}
                    </>
                  }
                  slotProps={{ secondary: { component: "div" } }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Box>
    </Box>
  );
}

export default function SectionTemplateViewerModal({
  open,
  onClose,
  sectionTemplate,
}) {
  const { openCD, setOpenCD, tca, setTca } = ContentDialogInit();

  useEffect(() => {
    if (!open) {
      setOpenCD(false);
      return;
    }

    setOpenCD(true);
    setTca({
      title: `Vorschau: ${sectionTemplate?.name ?? ""}`,
      maxWidth: "lg",
      content: (
        <SectionTemplateViewerContent sectionTemplate={sectionTemplate} />
      ),
      actions: null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sectionTemplate?.id]);

  return (
    <ContentDialog
      openCD={openCD}
      setOpenCD={setOpenCD}
      tca={tca}
      onClose={onClose}
    />
  );
}
